'use client'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useState, useCallback } from 'react'
import { CookieConsent } from '@/components/cookie-consent'
import { captureClickIds } from '@/lib/attribution'

export function PHProvider({ children }: { children: React.ReactNode }) {
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null)

  const initTracking = useCallback(() => {
    // Initialize PostHog
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        loaded: (posthog) => {
          // Only load after main thread is idle
          if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
              posthog.capture('$pageview')
            })
          }
        },
      })
    }

    // Enable Google Ads tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
      })
    }
  }, [])

  useEffect(() => {
    // Capture gclid/gbraid/wbraid immediately — the ad-click params are only
    // present on the landing URL and would be lost on the first navigation
    captureClickIds()

    // Defer analytics initialization to not block initial render
    const timeoutId = setTimeout(() => {
      // Check for existing consent
      const existingConsent = localStorage.getItem('cookie-consent')
      if (existingConsent === 'accepted') {
        setConsentGiven(true)
        initTracking()
      } else if (existingConsent === 'declined') {
        setConsentGiven(false)
      }
    }, 100) // Small delay to let main content render first

    return () => clearTimeout(timeoutId)
  }, [initTracking])

  const handleConsent = useCallback((accepted: boolean) => {
    setConsentGiven(accepted)
    if (accepted) {
      initTracking()
    }
  }, [initTracking])

  return (
    <PostHogProvider client={posthog}>
      {children}
      <CookieConsent onConsent={handleConsent} />
    </PostHogProvider>
  )
}
